/**
 * Save Draft utility - Saves composition as draft without processing/publishing
 * Creates sections (uploads files, applies trims) but skips video processing/merging
 */

import MeetingService from '../../../services/MeetingService';
import SectionService from '../../../services/SectionService';
import { store } from '../store';
import { processQueuedUploads, selectAllUploadStates } from '../store/slices/uploadProgressSlice';
import {
  createMediaSection,
  createAttachmentSections,
} from './publish';

/**
 * Saves a composition as draft to the backend
 * Creates sections (uploads files, applies trims) but skips processing/merging
 * @param {Object} params - Save draft parameters
 * @param {string} params.title - Meeting title
 * @param {string} params.folderId - Folder ID (optional)
 * @param {Array} params.assets - All assets (videos, images, documents, questions, texts, audios)
 * @param {Array} params.timelineItems - Timeline items in order
 * @param {string} params.meetingId - Existing meeting ID (for edit mode, optional)
 * @param {Function} params.onProgress - Progress callback (sectionIndex, totalSections, progress, operation)
 * @returns {Promise<Object>} - Meeting with draftData
 */
export async function saveDraftComposition({
  title,
  folderId,
  assets,
  timelineItems,
  meetingId: existingMeetingId,
  onProgress,
  userRole, // Optional: 'creator', 'contributor', 'contributor-closed'
  userId, // Optional: current user ID (for contributors to filter their sections)
  canUpdateDraftData = false, // Elevated users (org/folder owner, super admin) can update draftData even if meeting role is contributor
  pendingSaveItemIds, // Optional: Set of timeline item IDs marked for "Save Section" (favorite)
}) {
  const isLimitedContributor = userRole === 'contributor' && !canUpdateDraftData;

  // Clean up assigned section placeholders for contributors who have added content
  let filteredTimelineItems = timelineItems;
  if (isLimitedContributor && userId) {
    // Check if contributor has added any actual content (not assigned-section placeholders)
    const hasAddedContent = timelineItems.some(item => {
      const asset = assets.find(a => a.id === item.asset_id);
      return asset && asset.type !== 'assigned-section';
    });

    if (hasAddedContent) {
      // Remove all assigned-section placeholders assigned to this user
      filteredTimelineItems = timelineItems.filter(item => {
        const asset = assets.find(a => a.id === item.asset_id);
        if (!asset || asset.type !== 'assigned-section') return true;
        // Remove assigned-section items that belong to current user
        return asset.metadata?.assignedUserId !== userId;
      });
    }
  }

  // Filter items by type (same as publish.js)
  const videoItems = filteredTimelineItems.filter((item) => {
    const asset = assets.find((a) => a.id === item.asset_id);
    return asset && asset.type === 'video';
  });

  const imageItems = filteredTimelineItems.filter((item) => {
    const asset = assets.find((a) => a.id === item.asset_id);
    return asset && asset.type === 'image';
  });

  const documentItems = filteredTimelineItems.filter((item) => {
    const asset = assets.find((a) => a.id === item.asset_id);
    return asset && asset.type === 'document';
  });

  const questionItems = filteredTimelineItems.filter((item) => {
    const asset = assets.find((a) => a.id === item.asset_id);
    return asset && asset.type === 'question';
  });

  const textItems = filteredTimelineItems.filter((item) => {
    const asset = assets.find((a) => a.id === item.asset_id);
    return asset && asset.type === 'text';
  });

  const audioItems = filteredTimelineItems.filter((item) => {
    const asset = assets.find((a) => a.id === item.asset_id);
    return asset && asset.type === 'audio';
  });

  const assignedSectionItems = filteredTimelineItems.filter((item) => {
    const asset = assets.find((a) => a.id === item.asset_id);
    return asset && asset.type === 'assigned-section';
  });

  const placeholderItems = filteredTimelineItems.filter((item) => {
    const asset = assets.find((a) => a.id === item.asset_id);
    return asset && asset.type === 'placeholder';
  });

  const totalItems =
    videoItems.length +
    imageItems.length +
    documentItems.length +
    questionItems.length +
    textItems.length +
    audioItems.length +
    assignedSectionItems.length +
    placeholderItems.length;

  // Step 1: Create or use existing meeting
  if (onProgress) {
    onProgress(0, totalItems, 0, 'preparing');
  }

  let meeting;
  if (existingMeetingId) {
    // Edit mode: use existing meeting
    meeting = await MeetingService.getMeeting(existingMeetingId);

    // Update meeting title if changed
    if (meeting.title !== title) {
      await MeetingService.updateMeeting({
        meetingId: existingMeetingId,
        title,
      });
      meeting.title = title;
    }

    // Identify which sections to keep vs delete when re-saving
    // Only delete sections that are no longer in the timeline
    if (meeting.draftData?.sectionIds && meeting.draftData.sectionIds.length > 0) {
      // Get all sectionIds from current timeline items (from metadata and upload state)
      const currentSectionIds = new Set();
      const uploadStates = selectAllUploadStates(store.getState());
      
      filteredTimelineItems.forEach((item) => {
        // Check timeline item metadata first
        const sectionId = item.metadata?.sectionId;
        if (sectionId) {
          currentSectionIds.add(sectionId);
        }
        
        // Also check Redux upload state for completed background uploads
        const asset = assets.find((a) => a.id === item.asset_id);
        if (asset && uploadStates[asset.id]?.status === 'completed' && uploadStates[asset.id]?.sectionId) {
          currentSectionIds.add(uploadStates[asset.id].sectionId);
        }
      });

      // Delete only sections that are NOT in the current timeline
      const sectionsToDelete = meeting.draftData.sectionIds.filter(
        (sectionId) => !currentSectionIds.has(sectionId)
      );

      if (sectionsToDelete.length > 0) {
        try {
          await SectionService.deleteSectionsBatch({
            meetingId: existingMeetingId,
            ids: sectionsToDelete,
          });
        } catch (batchError) {
          // Fallback to sequential deletes if batch endpoint is unavailable
          // eslint-disable-next-line no-console
          console.warn('Batch section delete during save failed, falling back to sequential deletes', batchError);
          // eslint-disable-next-line no-restricted-syntax
          for (const sectionId of sectionsToDelete) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await SectionService.deleteSection({ sectionId });
            } catch (error) {
              const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
              throw new Error(`Failed to delete section during save: ${errorMessage}`);
            }
          }
        }
      }
    } else {
      // Fallback for meetings without draftData: delete sections not present in the current timeline
      try {
        const existingSections = await SectionService.getSections(existingMeetingId);
        if (existingSections && existingSections.length > 0) {
          const uploadStates = selectAllUploadStates(store.getState());
          const timelineSectionIds = new Set(
            filteredTimelineItems
              .map((item) => {
                const asset = assets.find((a) => a.id === item.asset_id);
                // Check metadata first
                if (asset?.source_type === 'project' && item.metadata?.sectionId) {
                  return item.metadata.sectionId;
                }
                // Also check Redux upload state for completed background uploads
                if (asset && uploadStates[asset.id]?.status === 'completed' && uploadStates[asset.id]?.sectionId) {
                  return uploadStates[asset.id].sectionId;
                }
                return null;
              })
              .filter((id) => id !== null)
          );

          const sectionsToDelete = existingSections
            .map((s) => s.id)
            .filter((id) => !timelineSectionIds.has(id));

          if (sectionsToDelete.length > 0) {
            try {
              await SectionService.deleteSectionsBatch({
                meetingId: existingMeetingId,
                ids: sectionsToDelete,
              });
            } catch (batchError) {
              // Fallback to sequential deletes if batch endpoint is unavailable
              // eslint-disable-next-line no-console
              console.warn('Batch section delete during save failed, falling back to sequential deletes', batchError);
              // eslint-disable-next-line no-restricted-syntax
              for (const sectionId of sectionsToDelete) {
                try {
                  // eslint-disable-next-line no-await-in-loop
                  await SectionService.deleteSection({ sectionId });
                } catch (error) {
                  const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
                  throw new Error(`Failed to delete section during save: ${errorMessage}`);
                }
              }
            }
          }
        }
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
        throw new Error(`Failed to sync deletions during save: ${errorMessage}`);
      }
    }
  } else {
    // Create mode: create new meeting
    meeting = await MeetingService.createMeeting({
      title,
      folderId: folderId || null,
      type: 'my videos',
    });

    // Process any queued uploads now that we have a meetingId
    store.dispatch(processQueuedUploads(meeting.id));
  }

  // Step 2: Create new sections and update existing ones
  // ARCHITECTURAL PRINCIPLE: Draft mode uses draftData.sectionIds array as source of truth
  // - Draft ordering: Uses only draftData.sectionIds array (array order = draft order)
  // - Section.order field: Synced from draftData.sectionIds by backend, not used for positioning
  // - No prevSectionId: Backend appends to draftData.sectionIds in timeline order
  const createdSectionIds = [];

  // Sort all items by their order in the timeline
  const allItems = [
    ...videoItems,
    ...audioItems,
    ...imageItems,
    ...documentItems,
    ...questionItems,
    ...textItems,
    ...assignedSectionItems,
    ...placeholderItems,
  ].sort((a, b) => a.order - b.order);

  // For contributors, only save sections they created (not assigned-sections or existing sections)
  // Filter items to only those created by the contributor
  const itemsToSave = isLimitedContributor && userId
    ? allItems.filter((item) => {
        const asset = assets.find((a) => a.id === item.asset_id);
        if (!asset) return false;
        // Contributors can save:
        // 1. New sections they're creating (source_type: 'upload' and not assigned-section)
        // 2. Existing sections they own (source_type: 'project' with matching userId) - for title/content updates
        if (asset.source_type === 'upload' && asset.type !== 'assigned-section') {
          return true;
        }
        if (asset.source_type === 'project' && asset.metadata?.userId === userId) {
          return true;
        }
        return false;
      })
    : allItems;

  // Calculate file size-based progress weights
  // Get file sizes for all items that have them (uploaded files)
  const itemSizes = itemsToSave.map((item) => {
    const asset = assets.find((a) => a.id === item.asset_id);
    // Only uploaded files have file_size; library-picked are server-side copied (fast)
    if (asset?.source_type === 'upload' && asset?.file_size) {
      return asset.file_size;
    }
    // For items without file_size (library-picked, questions, text), use small default
    // Library-picked are fast (server-side copy), questions/text are instant
    return 1; // Small default weight
  });

  const totalSize = itemSizes.reduce((sum, size) => sum + size, 0);
  
  // Calculate cumulative progress ranges for each section (0-80% range for section processing)
  const progressRanges = [];
  let cumulativeProgress = 0;
  itemsToSave.forEach((item, index) => {
    const size = itemSizes[index];
    const weight = totalSize > 0 ? size / totalSize : 1 / itemsToSave.length;
    const sectionRange = weight * 80; // 80% of total progress is for section processing
    progressRanges.push({
      start: cumulativeProgress,
      end: cumulativeProgress + sectionRange,
      range: sectionRange,
    });
    cumulativeProgress += sectionRange;
  });

  // Collect updates for existing sections (titles, text, trim, attachment metadata)
  const sectionUpdateMap = new Map(); // sectionId -> { sectionId, title?, text?, trimSettings?, attachment? }
  const existingSectionIds = new Set();

  const queueUpdate = (sectionId, data) => {
    if (!sectionId) return;
    const existing = sectionUpdateMap.get(sectionId) || { sectionId };
    sectionUpdateMap.set(sectionId, { ...existing, ...data });
  };

  // Build a lookup of existing sections if available
  let existingSections = [];
  if (existingMeetingId) {
    try {
      existingSections = await SectionService.getSections(existingMeetingId) || [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to fetch existing sections for updates during saveDraft:', error);
    }
  }
  const existingSectionMap = new Map(existingSections.map((s) => [s.id, s]));

  // Walk items to queue updates for persisted sections
  const processExistingItem = (item) => {
    const asset = assets.find((a) => a.id === item.asset_id);
    const { sectionId, attachmentId } = item.metadata || {};
    if (!asset || !sectionId) {
      return;
    }
    existingSectionIds.add(sectionId);
    const originalSection = existingSectionMap.get(sectionId);

    if (asset.type === 'placeholder') {
      const originalTitle = originalSection?.title || '';
      const originalText = originalSection?.text || '';
      const nextTitle = asset.name || '';
      const nextText = asset.metadata?.description || '';

      if (nextTitle && nextTitle !== originalTitle) {
        queueUpdate(sectionId, { title: nextTitle });
      }

      if (nextText !== originalText) {
        queueUpdate(sectionId, { text: nextText });
      }

      return;
    }

    if (asset.source_type !== 'project') {
      return;
    }

    const originalTitle = originalSection?.title || null;
    const titleChanged = asset.name && asset.name !== originalTitle;
    if (titleChanged) {
      queueUpdate(sectionId, { title: asset.name });
    }

    if (asset.type === 'video' || asset.type === 'audio') {
      const trimStart = item.settings?.trim_start ?? 0;
      const trimEnd = item.settings?.trim_end ?? 0;
      const originalDuration = asset.duration ?? (item.duration + trimStart + trimEnd);
      const originalTrim = originalSection?.trimSettings || null;
      const hasTrim = trimStart > 0 || trimEnd > 0;
      const trimChanged =
        (originalTrim?.trimStart ?? 0) !== trimStart ||
        (originalTrim?.trimEnd ?? 0) !== trimEnd ||
        (originalTrim?.originalDuration ?? originalDuration) !== originalDuration;

      if (trimChanged) {
        queueUpdate(sectionId, {
          trimSettings: hasTrim
            ? {
                trimStart,
                trimEnd,
                originalDuration,
              }
            : null,
        });
      }
    }

    if ((asset.type === 'image' || asset.type === 'document') && attachmentId) {
      const originalAttachment = originalSection?.SectionAttachments?.find((att) => att.id === attachmentId);
      const originalAttachmentName = originalAttachment?.fileName || originalAttachment?.name || '';
      const originalAttachmentDescription = originalAttachment?.description || '';
      const newAttachmentName = asset.metadata?.attachmentName || asset.name || '';
      const newAttachmentDescription = asset.metadata?.attachmentDescription || '';

      const attachmentChanged =
        newAttachmentName !== originalAttachmentName || newAttachmentDescription !== originalAttachmentDescription;

      if (attachmentChanged) {
        queueUpdate(sectionId, {
          attachment: {
            id: attachmentId,
            name: newAttachmentName,
            description: newAttachmentDescription,
          },
        });
      }
    }
  };

  allItems.forEach(processExistingItem);

  // Apply batch updates first for existing sections (no-op if none)
  const sectionUpdates = Array.from(sectionUpdateMap.values());
  if (sectionUpdates.length > 0) {
    try {
      await SectionService.updateSectionsBatch({
        meetingId: meeting.id,
        items: sectionUpdates,
      });
    } catch (batchError) {
      // eslint-disable-next-line no-console
      console.warn('Batch section update during save failed, falling back to sequential updates', batchError);
      // eslint-disable-next-line no-restricted-syntax
      for (const update of sectionUpdates) {
        const {
          sectionId,
          title: updateTitle,
          text: updateText,
          trimSettings,
          attachment,
        } = update;
        if (updateTitle) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await SectionService.updateSection({
              sectionId,
              title: updateTitle,
            });
          } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
            throw new Error(`Failed to update section title "${updateTitle}" during save: ${errorMessage}`);
          }
        }
        if (updateText !== undefined) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await SectionService.updateSection({
              sectionId,
              text: updateText,
            });
          } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
            throw new Error(`Failed to update section text during save: ${errorMessage}`);
          }
        }
        if (trimSettings !== undefined) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await SectionService.updateSection({
              sectionId,
              trimSettings,
            });
          } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
            throw new Error(`Failed to update trim settings during save: ${errorMessage}`);
          }
        }
        if (attachment) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await SectionService.updateSectionAttachment({
              sectionId,
              attachment,
            });
          } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
            throw new Error(`Failed to update attachment metadata during save: ${errorMessage}`);
          }
        }
      }
    }
  }

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < itemsToSave.length; i += 1) {
    const item = itemsToSave[i];
    const asset = assets.find((a) => a.id === item.asset_id);
    const progressRange = progressRanges[i];

    if (asset) {
      if (asset.type === 'placeholder') {
        const { sectionId } = item.metadata || {};

        if (!sectionId) {
          throw new Error(
            'Placeholder sections in videos must already exist on the server before saving. Remove the placeholder or replace it with real content and try again.'
          );
        }

        if (onProgress) {
          onProgress(i + 1, totalItems, Math.round(progressRange.end), 'creating-section');
        }

        createdSectionIds.push(sectionId);
        // eslint-disable-next-line no-continue
        continue;
      }

      // Handle assigned-section sections
      if (asset.type === 'assigned-section') {
        // If already created (from previous save), reuse it
        if (asset.source_type === 'project' && item.metadata?.sectionId) {
          const { sectionId } = item.metadata;
          createdSectionIds.push(sectionId);
          // Backend will sync order from draftData.sectionIds array
          // eslint-disable-next-line no-continue
          continue;
        }

        // New assigned section (source_type: 'upload') - create it now
        if (asset.source_type === 'upload') {
          const assignedEmail = asset.metadata?.assignedEmail || item.metadata?.assignedEmail;
          const message = asset.metadata?.message || item.metadata?.message || '';

          if (!assignedEmail) {
            // eslint-disable-next-line no-console
            console.warn('Assigned section missing email, skipping');
        // eslint-disable-next-line no-continue
        continue;
          }

          // Update progress for assigned section creation (instant, uses size-based range)
          if (onProgress) {
            onProgress(i + 1, totalItems, Math.round(progressRange.start), 'creating-assigned-section');
          }

          // Determine prevSectionId from timeline order (last section in createdSectionIds)
          // Backend uses this to insert section at correct position in draftData.sectionIds
          const prevSectionId = createdSectionIds.length > 0 ? createdSectionIds[createdSectionIds.length - 1] : null;

          // Call assign-section API to create the section
          // Backend will add to draftData.sectionIds and sync order
          // eslint-disable-next-line no-await-in-loop
          const response = await MeetingService.assignSection({
            meetingId: meeting.id,
            email: assignedEmail,
            message,
            prevSectionId,
          });

          if (response.section) {
            const { section } = response;
            createdSectionIds.push(section.id);
          } else {
            // eslint-disable-next-line no-console
            console.warn('Failed to create assigned section, response missing section object');
          }

          // Update progress after assigned section is created (instant)
          if (onProgress) {
            onProgress(i + 1, totalItems, Math.round(progressRange.end), 'creating-section');
          }
          // eslint-disable-next-line no-continue
          continue;
        }
      }

      // If item already has a sectionId (from loaded draft), reuse it instead of creating new
      if (asset.source_type === 'project' && item.metadata?.sectionId) {
        // This is an existing section from the draft - reuse it
        const { sectionId } = item.metadata;

        // Updates (title/trim/attachments) are handled in the batch phase above.
        createdSectionIds.push(sectionId);
        // Backend will sync order from draftData.sectionIds array
        // eslint-disable-next-line no-continue
        continue;
      }

      // Handle library-picked videos (projectMeetingId) - use server-side copy
      if (asset.source_type === 'project' && asset.metadata?.projectMeetingId) {
        // Determine operation type based on trim settings
        const trimStart = item.settings?.trim_start ?? 0;
        const trimEnd = item.settings?.trim_end ?? 0;
        const operation = (trimStart > 0 || trimEnd > 0) ? 'copying-and-trimming' : 'copying-from-library';

        // Update progress: server-side copy is fast (uses size-based range, but library-picked have small weight)
        if (onProgress) {
          onProgress(i + 1, totalItems, Math.round(progressRange.start), operation);
        }

        // Determine prevSectionId from timeline order (last section in createdSectionIds)
        // Backend uses this to insert section at correct position in draftData.sectionIds
        const prevSectionId = createdSectionIds.length > 0 ? createdSectionIds[createdSectionIds.length - 1] : null;

        // Get source section ID from the library meeting
        // Saved sections are stored as meetings with typically one section
        // eslint-disable-next-line no-await-in-loop
        const sourceSections = await SectionService.getSections(asset.metadata.projectMeetingId);
        if (!sourceSections || sourceSections.length === 0) {
          // eslint-disable-next-line no-console
          console.error(`No sections found in library meeting ${asset.metadata.projectMeetingId}`);
          // eslint-disable-next-line no-continue
          continue;
        }
        const sourceSectionId = sourceSections[0].id;

        // Use server-side copy instead of client-side download/re-upload
        // Backend will handle S3 copy (untrimmed) or MediaConvert (trimmed)
        // eslint-disable-next-line no-await-in-loop
        const section = await SectionService.copySectionFromLibrary({
          sourceSectionId,
          meetingId: meeting.id,
          title: asset.name,
          trimStart,
          trimEnd,
          prevSectionId,
        });

        createdSectionIds.push(section.id);

        // Update trim settings for this section
        const trimSettings = (trimStart > 0 || trimEnd > 0) ? {
          trimStart,
          trimEnd,
          originalDuration: asset.duration ?? (item.duration + trimStart + trimEnd),
        } : null;
        
        if (trimSettings !== null) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await SectionService.updateSection({
              sectionId: section.id,
              trimSettings,
            });
          } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
            throw new Error(`Failed to update section trim settings during save: ${errorMessage}`);
          }
        }

        // Update progress after section is created (server-side copy is fast)
        if (onProgress) {
          onProgress(i + 1, totalItems, Math.round(progressRange.end), 'creating-section');
        }

        // Save section to library if user marked it (e.g., mobile uploads imported from library)
        if (pendingSaveItemIds && pendingSaveItemIds.has && pendingSaveItemIds.has(item.id)) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await SectionService.saveSection(section.id);
          } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
            throw new Error(`Failed to save section to library during save: ${errorMessage}`);
          }
        }
      } else if (asset.source_type === 'upload') {
        // Check if upload is already completed via background upload
        const uploadStates = selectAllUploadStates(store.getState());
        const uploadState = uploadStates[asset.id];
        if (uploadState && uploadState.status === 'completed') {
          // Upload already completed! Just link the existing section
          const { sectionId } = uploadState;
          createdSectionIds.push(sectionId);

          // Update section metadata (title, trim settings)
          if (asset.type === 'video' || asset.type === 'audio') {
            const trimStart = item.settings?.trim_start ?? 0;
            const trimEnd = item.settings?.trim_end ?? 0;
            const trimSettings = (trimStart > 0 || trimEnd > 0) ? {
              trimStart,
              trimEnd,
              originalDuration: asset.duration ?? (item.duration + trimStart + trimEnd),
            } : null;

            if (trimSettings !== null) {
              try {
                // eslint-disable-next-line no-await-in-loop
                await SectionService.updateSection({
                  sectionId,
                  trimSettings,
                });
              } catch (error) {
                const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
                throw new Error(`Failed to update section trim settings during save: ${errorMessage}`);
              }
            }
          }

          // Save section to library if user marked it
          if (pendingSaveItemIds && pendingSaveItemIds.has && pendingSaveItemIds.has(item.id)) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await SectionService.saveSection(sectionId);
            } catch (error) {
              const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
              throw new Error(`Failed to save section to library during save: ${errorMessage}`);
            }
          }

          // Update progress
          if (onProgress) {
            onProgress(i + 1, totalItems, Math.round(progressRange.end), 'creating-section');
          }
          // eslint-disable-next-line no-continue
          continue; // Skip to next item
        }

        // Determine operation type based on asset type
        let operation = 'creating-section';
        if (asset.type === 'video') {
          operation = 'uploading-video';
        } else if (asset.type === 'audio') {
          operation = 'uploading-audio';
        } else if (asset.type === 'image') {
          operation = 'uploading-image';
        } else if (asset.type === 'document') {
          operation = 'uploading-document';
        } else if (asset.type === 'question') {
          operation = 'creating-question';
        } else if (asset.type === 'text') {
          operation = 'creating-text';
        }

        // New uploaded asset - create section
        // Use size-based weighted progress range
        // Check if file needs remuxing (MOV files or other non-MP4/WebM formats)
        const needsRemuxing = asset.type === 'video' && 
          asset.mime_type && 
          !['video/mp4', 'video/webm'].includes(asset.mime_type);
        
        // Determine operation based on asset type and remuxing status
        let currentOperation = operation;
        if (needsRemuxing && asset.type === 'video') {
          currentOperation = 'uploading-video'; // Will change to 'remuxing' later
        }
        
        const sectionProgress = (progress) => {
          // progress is 0-100 from createMediaSection
          // For remuxing files: 0-70% upload, 70-90% section creation, 90-100% remuxing
          // For non-remuxing files: 0-80% upload, 80-100% section creation
          // Map it to this section's allocated range
          const sectionProgressPercent = (progress / 100) * progressRange.range;
          const totalProgress = progressRange.start + sectionProgressPercent;
          
          // Determine operation status based on progress
          let progressOperation = currentOperation;
          if (needsRemuxing) {
            if (progress >= 90) {
              progressOperation = 'remuxing';
            } else if (progress >= 70) {
              progressOperation = 'creating-section';
            } else {
              progressOperation = 'uploading-video';
            }
          } else if (progress >= 80) {
            progressOperation = 'creating-section';
          } else {
            progressOperation = currentOperation;
          }
          
          if (onProgress) {
            onProgress(i + 1, totalItems, Math.round(totalProgress), progressOperation);
          }
        };

        // Determine prevSectionId from timeline order (last section in createdSectionIds)
        // Backend uses this to insert section at correct position in draftData.sectionIds
        const prevSectionId = createdSectionIds.length > 0 ? createdSectionIds[createdSectionIds.length - 1] : null;

        if (asset.type === 'video' || asset.type === 'audio') {
          // eslint-disable-next-line no-await-in-loop
          const section = await createMediaSection({
            meetingId: meeting.id,
            asset,
            timelineItem: item,
            prevSectionId,
            onUploadProgress: sectionProgress,
          });

          // Update progress to 100% when section creation completes
          // (this includes remuxing if it was needed)
          if (onProgress) {
            onProgress(i + 1, totalItems, Math.round(progressRange.end), 'creating-section');
          }

          createdSectionIds.push(section.id);

          // Update trim settings for this section (for uploaded video/audio)
          const trimStart = item.settings?.trim_start ?? 0;
          const trimEnd = item.settings?.trim_end ?? 0;
          const trimSettings = (trimStart > 0 || trimEnd > 0) ? {
            trimStart,
            trimEnd,
            originalDuration: asset.duration ?? (item.duration + trimStart + trimEnd),
          } : null;

          if (trimSettings !== null) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await SectionService.updateSection({
                sectionId: section.id,
                trimSettings,
              });
            } catch (error) {
              const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
              throw new Error(`Failed to update section trim settings during save: ${errorMessage}`);
            }
          }

          // Save section to library if user marked it
          if (pendingSaveItemIds && pendingSaveItemIds.has && pendingSaveItemIds.has(item.id)) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await SectionService.saveSection(section.id);
            } catch (error) {
              const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
              throw new Error(`Failed to save section to library during save: ${errorMessage}`);
            }
          }
        } else if (asset.type === 'image' || asset.type === 'document') {
          // eslint-disable-next-line no-await-in-loop
          const section = await createAttachmentSections({
            meetingId: meeting.id,
            assets: [asset],
            timelineItems: [item],
            prevSectionId,
            onUploadProgress: sectionProgress,
          });

          createdSectionIds.push(section.id);

          // Save section to library if user marked it
          if (pendingSaveItemIds && pendingSaveItemIds.has && pendingSaveItemIds.has(item.id)) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await SectionService.saveSection(section.id);
            } catch (error) {
              const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
              throw new Error(`Failed to save section to library during save: ${errorMessage}`);
            }
          }
        } else if (asset.type === 'question') {
          // Update progress for question creation (instant, uses size-based range)
          if (onProgress) {
            onProgress(i + 1, totalItems, Math.round(progressRange.start), operation);
          }

          // eslint-disable-next-line no-await-in-loop
          const section = await SectionService.createSection({
            meetingId: meeting.id,
            title: asset.name,
            prevSectionId,
            question: {
              isAnswerRequired: !!asset.isAnswerRequired,
              text: asset.text,
              options: asset.options || null,
            },
          });

          createdSectionIds.push(section.id);

          // Save section to library if user marked it
          if (pendingSaveItemIds && pendingSaveItemIds.has && pendingSaveItemIds.has(item.id)) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await SectionService.saveSection(section.id);
            } catch (error) {
              const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
              throw new Error(`Failed to save section to library during save: ${errorMessage}`);
            }
          }
        } else if (asset.type === 'text') {
          // Update progress for text creation (instant, uses size-based range)
          if (onProgress) {
            onProgress(i + 1, totalItems, Math.round(progressRange.start), operation);
          }

          // eslint-disable-next-line no-await-in-loop
          const section = await SectionService.createSection({
            meetingId: meeting.id,
            title: asset.name || 'Text',
            prevSectionId,
            text: asset.text || '',
          });

          createdSectionIds.push(section.id);

          // Save section to library if user marked it
          if (pendingSaveItemIds && pendingSaveItemIds.has && pendingSaveItemIds.has(item.id)) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await SectionService.saveSection(section.id);
            } catch (error) {
              const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
              throw new Error(`Failed to save section to library during save: ${errorMessage}`);
            }
          }
        }

        // Update progress after section is created (instant sections)
        if (onProgress && (asset.type === 'question' || asset.type === 'text')) {
          onProgress(i + 1, totalItems, Math.round(progressRange.end), 'creating-section');
        }
      }
    }
  }

  // Step 3: Update meeting with draftData containing section IDs
  // NOTE: Contributors cannot update draftData (they're not meeting owners)
  // They can only create new sections, which are automatically added to the meeting
  // The creator will see these sections when they load the draft
  if (isLimitedContributor) {
    // Contributors can only create sections, not update draftData
    // Refresh meeting so draftData.sectionIds contains the new sections
    let refreshedMeeting = meeting;
    if (meeting?.id) {
      try {
        refreshedMeeting = await MeetingService.getMeeting(meeting.id);
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
        throw new Error(`Failed to refresh meeting data during save: ${errorMessage}`);
      }
    }

    if (onProgress) {
      onProgress(itemsToSave.length, itemsToSave.length, 100);
    }
    return refreshedMeeting;
  }

  // For creators: Update meeting with draftData containing section IDs
  // Backend will automatically sync Section.order to match draftData.sectionIds array order
  const draftData = {
    version: '1.0.0',
    sectionIds: createdSectionIds,
    savedAt: new Date().toISOString(),
  };

  if (onProgress) {
    onProgress(totalItems, totalItems, 90, 'finalizing');
  }

  const updatedMeeting = await MeetingService.updateMeeting({
    meetingId: meeting.id,
    draftData,
  });

  if (onProgress) {
    onProgress(totalItems, totalItems, 100, 'finalizing');
  }

  return updatedMeeting;
}
